// /app/frontend/src/components/SuccessStories.jsx
import React, { useState } from 'react';
import { Star, Quote, TrendingUp, Clock, DollarSign, MapPin, ChevronLeft, ChevronRight, Home } from 'lucide-react';

const successStories = [
  {
    id: 1,
    name: "Michael & Jennifer Thompson",
    city: "Frisco",
    neighborhood: "Stonebriar",
    propertyType: "4BR/3BA Single Family",
    listPrice: 485000,
    salePrice: 492000,
    daysOnMarket: 8,
    savedCommission: 29520,
    year: 2024,
    story: "We were nervous about selling FSBO, but Its-Sold.com made it incredibly easy. Within a week, we had multiple offers and sold for $7,000 over asking price!",
    highlight: "Sold $7K over asking",
    avatar: "MT",
    rating: 5,
    timeline: [
      { day: 1, event: "Listed on Its-Sold.com" },
      { day: 2, event: "12 showing requests" },
      { day: 5, event: "3 offers received" },
      { day: 8, event: "Closed above asking!" }
    ]
  },
  {
    id: 2,
    name: "David Chen",
    city: "Plano",
    neighborhood: "Legacy West",
    propertyType: "3BR/2BA Townhome",
    listPrice: 375000,
    salePrice: 375000,
    daysOnMarket: 14,
    savedCommission: 22500,
    year: 2024,
    story: "After paying $18K in commission on my last home sale, I decided to try FSBO. Its-Sold.com's marketing tools helped me reach serious buyers directly. Best decision ever!",
    highlight: "Saved $22,500 in commission",
    avatar: "DC",
    rating: 5,
    timeline: [
      { day: 1, event: "Listed with pro photos" },
      { day: 7, event: "Open house - 23 visitors" },
      { day: 10, event: "Accepted qualified offer" },
      { day: 14, event: "Smooth closing!" }
    ]
  },
  {
    id: 3,
    name: "The Rodriguez Family",
    city: "McKinney",
    neighborhood: "Craig Ranch",
    propertyType: "5BR/4BA Estate Home",
    listPrice: 725000,
    salePrice: 718000,
    daysOnMarket: 21,
    savedCommission: 43080,
    year: 2024,
    story: "Selling a luxury home FSBO seemed daunting, but the platform's exposure was amazing. We saved over $43,000 that went straight toward our new home down payment.",
    highlight: "Saved $43K on luxury sale",
    avatar: "RF",
    rating: 5,
    timeline: [
      { day: 1, event: "Premium listing created" },
      { day: 7, event: "Featured in DFW searches" },
      { day: 14, event: "Negotiated with 2 buyers" },
      { day: 21, event: "Closed with cash buyer!" }
    ]
  },
  {
    id: 4,
    name: "Sarah Mitchell",
    city: "Arlington",
    neighborhood: "Viridian",
    propertyType: "3BR/2BA New Build",
    listPrice: 340000,
    salePrice: 345000,
    daysOnMarket: 11,
    savedCommission: 20700,
    year: 2025,
    story: "As a first-time seller, I appreciated how straightforward the process was. The step-by-step guidance and document templates saved me time and money!",
    highlight: "First-time seller success",
    avatar: "SM",
    rating: 5,
    timeline: [
      { day: 1, event: "Easy listing setup" },
      { day: 4, event: "Professional photos added" },
      { day: 8, event: "Multiple offers!" },
      { day: 11, event: "Above-asking close" }
    ]
  },
  {
    id: 5,
    name: "Robert & Maria Gonzalez",
    city: "Grand Prairie",
    neighborhood: "Mira Lagos",
    propertyType: "4BR/3BA Two-Story",
    listPrice: 395000,
    salePrice: 390000,
    daysOnMarket: 18,
    savedCommission: 23400,
    year: 2025,
    story: "We relocated for work and needed to sell fast. Its-Sold.com's DFW-focused marketing meant we reached local buyers quickly. Closed in under 3 weeks!",
    highlight: "Quick relocation sale",
    avatar: "RG",
    rating: 5,
    timeline: [
      { day: 1, event: "Urgent listing posted" },
      { day: 5, event: "Price optimized with AI" },
      { day: 12, event: "Accepted strong offer" },
      { day: 18, event: "Fast close complete!" }
    ]
  },
  {
    id: 6,
    name: "Amanda Foster",
    city: "Southlake",
    neighborhood: "Timarron",
    propertyType: "5BR/5BA Luxury Estate",
    listPrice: 1250000,
    salePrice: 1225000,
    daysOnMarket: 35,
    savedCommission: 73500,
    year: 2024,
    story: "Agents wanted 6% on my $1.2M home - that's $75K! With Its-Sold.com, I marketed directly to qualified buyers and kept that money for my retirement.",
    highlight: "Saved $73,500 on luxury home",
    avatar: "AF",
    rating: 5,
    timeline: [
      { day: 1, event: "Premium luxury listing" },
      { day: 14, event: "Exclusive showings" },
      { day: 28, event: "Negotiated with buyer" },
      { day: 35, event: "Million-dollar close!" }
    ]
  }
];

const SuccessStories = () => {
  const [activeStory, setActiveStory] = useState(0);
  const [showTimeline, setShowTimeline] = useState(false);

  const story = successStories[activeStory];

  const nextStory = () => {
    setActiveStory((prev) => (prev + 1) % successStories.length);
    setShowTimeline(false);
  };

  const prevStory = () => {
    setActiveStory((prev) => (prev - 1 + successStories.length) % successStories.length);
    setShowTimeline(false);
  };

  const totalSaved = successStories.reduce((acc, s) => acc + s.savedCommission, 0);
  const avgDays = Math.round(successStories.reduce((acc, s) => acc + s.daysOnMarket, 0) / successStories.length);

  return (
    <section className="py-16 bg-gradient-to-b from-gray-50 to-white" data-testid="success-stories-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Real DFW Sellers. Real Savings.
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Join thousands of Texas homeowners who've saved money selling FSBO with Its-Sold.com
          </p>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 text-center">
            <div className="text-2xl sm:text-3xl font-bold text-orange-600">${(totalSaved / 1000).toFixed(0)}K+</div>
            <div className="text-sm text-gray-600">Saved in Commissions</div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 text-center">
            <div className="text-2xl sm:text-3xl font-bold text-green-600">{avgDays} Days</div>
            <div className="text-sm text-gray-600">Avg. Time to Sell</div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 text-center">
            <div className="text-2xl sm:text-3xl font-bold text-blue-600">27+</div>
            <div className="text-sm text-gray-600">Years Experience</div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 text-center">
            <div className="text-2xl sm:text-3xl font-bold text-purple-600">5.0</div>
            <div className="text-sm text-gray-600">Average Rating</div>
          </div>
        </div>

        {/* Main Story Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
          <div className="md:flex">
            {/* Left Side - Story Info */}
            <div className="md:w-1/2 p-6 sm:p-8">
              {/* Quote Icon */}
              <div className="mb-4">
                <Quote className="w-10 h-10 text-orange-500 opacity-50" />
              </div>

              {/* Testimonial */}
              <p className="text-lg sm:text-xl text-gray-700 italic mb-6 leading-relaxed">
                "{story.story}"
              </p>

              {/* Seller Info */}
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  {story.avatar}
                </div>
                <div>
                  <div className="font-semibold text-gray-900">{story.name}</div>
                  <div className="flex items-center text-gray-600 text-sm">
                    <MapPin className="w-4 h-4 mr-1" />
                    {story.city}, TX • {story.neighborhood}
                  </div>
                </div>
              </div>

              {/* Rating */}
              <div className="flex items-center gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`w-5 h-5 ${i < story.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
                  />
                ))}
                <span className="ml-2 text-sm text-gray-600">Verified Seller</span>
              </div>

              {/* Highlight Badge */}
              <div className="inline-flex items-center px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                <TrendingUp className="w-4 h-4 mr-2" />
                {story.highlight}
              </div>
            </div>

            {/* Right Side - Stats & Timeline */}
            <div className="md:w-1/2 bg-gradient-to-br from-gray-900 to-gray-800 p-6 sm:p-8 text-white">
              {/* Property Details */}
              <div className="mb-6">
                <div className="flex items-center gap-2 text-orange-400 mb-2">
                  <Home className="w-5 h-5" />
                  <span className="font-medium">{story.propertyType}</span>
                </div>
                <div className="text-sm text-gray-400">{story.year} Sale</div>
              </div>

              {/* Sale Stats */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-gray-800/50 rounded-lg p-4">
                  <div className="text-gray-400 text-xs uppercase tracking-wider mb-1">List Price</div>
                  <div className="text-xl font-bold">${story.listPrice.toLocaleString()}</div>
                </div>
                <div className="bg-gray-800/50 rounded-lg p-4">
                  <div className="text-gray-400 text-xs uppercase tracking-wider mb-1">Sale Price</div>
                  <div className="text-xl font-bold text-green-400">${story.salePrice.toLocaleString()}</div>
                </div>
                <div className="bg-gray-800/50 rounded-lg p-4">
                  <div className="text-gray-400 text-xs uppercase tracking-wider mb-1">Days on Market</div>
                  <div className="text-xl font-bold flex items-center">
                    <Clock className="w-5 h-5 mr-2 text-blue-400" />
                    {story.daysOnMarket}
                  </div>
                </div>
                <div className="bg-gray-800/50 rounded-lg p-4">
                  <div className="text-gray-400 text-xs uppercase tracking-wider mb-1">Commission Saved</div>
                  <div className="text-xl font-bold text-orange-400 flex items-center">
                    <DollarSign className="w-5 h-5" />
                    {story.savedCommission.toLocaleString()}
                  </div>
                </div>
              </div>

              {/* Timeline Toggle */}
              <button
                onClick={() => setShowTimeline(!showTimeline)}
                className="w-full py-3 px-4 bg-orange-600 hover:bg-orange-500 rounded-lg font-medium transition-colors mb-4"
                data-testid="toggle-timeline-btn"
              >
                {showTimeline ? 'Hide Timeline' : 'View Sale Timeline'}
              </button>

              {/* Timeline */}
              {showTimeline && (
                <div className="space-y-3 animate-fadeIn">
                  {story.timeline.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-orange-600 rounded-full flex items-center justify-center text-sm font-bold">
                        {item.day}
                      </div>
                      <div className="flex-1 bg-gray-800/50 rounded-lg px-4 py-2 text-sm">
                        {item.event}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border-t border-gray-100">
            <button
              onClick={prevStory}
              className="flex items-center gap-2 text-gray-600 hover:text-orange-600 transition-colors"
              data-testid="prev-story-btn"
            >
              <ChevronLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Previous</span>
            </button>

            {/* Dots */}
            <div className="flex gap-2">
              {successStories.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => { setActiveStory(idx); setShowTimeline(false); }}
                  className={`w-2.5 h-2.5 rounded-full transition-colors ${
                    idx === activeStory ? 'bg-orange-600' : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                  data-testid={`story-dot-${idx}`}
                />
              ))}
            </div>

            <button
              onClick={nextStory}
              className="flex items-center gap-2 text-gray-600 hover:text-orange-600 transition-colors"
              data-testid="next-story-btn"
            >
              <span className="hidden sm:inline">Next</span>
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <p className="text-gray-600 mb-4">Ready to write your own success story?</p>
          <a
            href="/register"
            className="inline-flex items-center px-8 py-4 bg-orange-600 hover:bg-orange-500 text-white font-semibold rounded-xl transition-colors shadow-lg hover:shadow-xl"
            data-testid="success-stories-cta"
          >
            List Your Home Free Today
          </a>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </section>
  );
};

export default SuccessStories;
